import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import Hls from 'hls.js';
import { isBeta } from '../../shared/services';
import { StoreHelper } from '../../shared/helpers/store.helper';
import { ControllerService } from '../../shared/services/controller.service';
import { BunnyMapService } from '../../shared/services/bunny-map.service';

@Component({
  selector: 'app-bunny-videos',
  templateUrl: './bunny-videos.component.html',
  styleUrls: ['./bunny-videos.component.scss'],
  standalone: false
})
export class BunnyVideosComponent implements OnInit, OnDestroy {

  @ViewChild('videoEl', { static: true }) videoElRef!: ElementRef<HTMLVideoElement>;

  private get video(): HTMLVideoElement {
    return this.videoElRef.nativeElement;
  }

  items: string[] = [];
  current: number = 0;
  duration: number = 0;
  progressValue: number = 0;
  underPlay: boolean = false;
  endOfLastVideo: boolean = false;
  OnCaption: boolean = false;

  lastEvent: any;
  currentVimeoId: string = '';
  firstLoad: boolean = false;
  lastUpdated: number = 0;
  courseProgress: number = 0;
  updateInterval: number = 1000;
  isBeta: boolean = isBeta();
  posterUrl: string = '';
  introText: string = '';
  introHeader: string = '';
  activeNext: boolean = false;
  skipToEnd: boolean = false;

  private hls: Hls | null = null;

  constructor(
    public controller: ControllerService,
    private bunnyMapService: BunnyMapService
  ) {}

  ngOnDestroy(): void {
    this.hls?.destroy();
    document.querySelectorAll<HTMLElement>('.main-link').forEach(el => el.style.display = '');
    this.controller.toggleLoading(false);
  }

  ngOnInit() {
    this.skipToEnd = StoreHelper.getObject('fastForward');
    this.controller.toggleLoading(false);

    document.getElementById('video-with-controls')?.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement === null) {
        this.underPlay = false;
        this.video.pause();
      }
    });

    this.introText = this.controller.getSectionProperties().introText;
    this.introHeader = this.controller.getSectionProperties().introHeader;

    this.items = this.controller.getSectionComponents().map(
      (component: any) => component.properties.bunnyId || component.properties.vimeoId
    );

    this.loadVideo(true);
  }

  private loadVideo(firstLoad = false) {
    this.firstLoad = firstLoad;
    this.lastEvent = new Date().getTime();

    if (!this.items.length) return;

    this.controller.toggleLoading(true);

    this.current = 0;
    this.currentVimeoId = this.items.pop()!;
    const bunnyId = this.bunnyMapService.getBunnyId(this.currentVimeoId) ?? this.currentVimeoId;
    const src = this.bunnyMapService.getPlaylistUrl(bunnyId);
    this.posterUrl = this.bunnyMapService.getThumbnailUrl(bunnyId);

    this.hls?.destroy();

    if (Hls.isSupported()) {
      this.hls = new Hls();
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.controller.toggleLoading(false);
        if (!firstLoad) {
          this.onPlay();
        }
      });
      this.video.addEventListener('loadedmetadata', () => {
        this.duration = this.video.duration;
      }, { once: true });
      this.hls.loadSource(src);
      this.hls.attachMedia(this.video);
    } else {
      this.video.src = src;
      this.video.addEventListener('loadedmetadata', () => {
        this.controller.toggleLoading(false);
        this.duration = this.video.duration;
        if (!firstLoad) {
          this.onPlay();
        }
      }, { once: true });
    }

    // Replace caption track for the new video
    Array.from(this.video.querySelectorAll('track')).forEach(t => t.remove());
    const captionTrack = document.createElement('track');
    captionTrack.kind = 'captions';
    captionTrack.label = 'English';
    captionTrack.srclang = 'GB';
    captionTrack.src = this.bunnyMapService.getCaptionsUrl(bunnyId);
    captionTrack.addEventListener('error', () => {
      captionTrack.src = this.bunnyMapService.getCaptionsUrl(bunnyId, 'EN');
    }, { once: true });
    this.video.appendChild(captionTrack);

    this.video.volume = 1;

    this.video.addEventListener('timeupdate', () => {
      this.controller.toggleLoading(false);
      if (this.progressValue > 0) {
        document.querySelector('.play-next-wrapper')?.classList.add('hide');
      }
      const current = this.video.currentTime;
      const duration = this.video.duration;
      const percent = duration ? current / duration : 0;

      if (this.lastEvent && this.lastEvent < new Date().getTime() - 5000) {
        this.lastEvent = new Date().getTime();
        this.fadeOutControls();
      }

      if (!this.lastUpdated || this.lastUpdated + this.updateInterval < Date.now()) {
        this.lastUpdated = Date.now();
        this.onMediaTimeUpdate({ seconds: current, percent, duration });
      }
    });

    this.video.addEventListener('ended', () => {
      this.current = this.duration;
      this.onMediaEnd();
    });
  }

  onMediaTimeUpdate(data: { seconds: number; percent: number; duration: number }) {
    this.current = data.seconds;
    this.duration = data.duration;
    this.progressValue = data.percent * 100;
  }

  onClickProgress(e: MouseEvent) {
    const target = e.target as HTMLElement;
    const x = e.pageX - target.offsetLeft;
    const clickedValue = x / target.clientWidth;
    if (clickedValue < 1) {
      const newTime = Math.floor(clickedValue * this.duration);
      this.video.currentTime = newTime;
      this.onMediaTimeUpdate({ seconds: newTime, duration: this.duration, percent: newTime / this.duration });
    }
  }

  onMediaEnd() {
    if (this.items.length === 0) {
      this.underPlay = false;
      this.endOfLastVideo = true;
      this.activeNext = true;
      document.exitFullscreen().catch(() => {});
    }
    this.loadVideo(false);
    document.querySelectorAll<HTMLElement>('.main-link').forEach(el => el.style.display = '');
    this.controller.next();
  }

  onPlay() {
    document.querySelector('.play-next-wrapper')?.classList.add('hide');
    if (this.firstLoad) {
      this.controller.toggleLoading(true);
    }
    this.underPlay = true;
    this.video.play().catch(() => {
      this.onPause();
    });
    this.videobgEnlarge();
    setTimeout(() => this.videobgEnlarge(), 10);
    document.querySelectorAll<HTMLElement>('.main-link').forEach(el => el.style.display = 'none');
    document.getElementById('video-with-controls')?.requestFullscreen();

    if (this.skipToEnd) {
      const dur = this.video.duration;
      if (dur) {
        this.video.currentTime = dur - 5;
      } else {
        this.video.addEventListener('loadedmetadata', () => {
          this.video.currentTime = this.video.duration - 5;
        }, { once: true });
      }
    }

    if (!this.OnCaption) {
      this.OffSubtitles();
    } else {
      this.OnSubtitles();
    }
  }

  OnSubtitles() {
    const tracks = this.video.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      if (track.kind === 'subtitles' || track.kind === 'captions') {
        track.mode = 'showing';
        break;
      }
    }
    this.OnCaption = true;
  }

  OffSubtitles() {
    const tracks = this.video.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = 'disabled';
    }
    this.OnCaption = false;
  }

  onBackward() {
    if (this.underPlay && this.current - 5 > 0) {
      this.current -= 5;
      this.video.currentTime = this.current;
    }
  }

  onForward() {
    if (this.underPlay && (this.current + 5) < this.duration) {
      this.current += 5;
      this.video.currentTime = this.current;
    }
  }

  onPause() {
    this.underPlay = false;
    this.video.pause();
    document.exitFullscreen().catch(() => {});
  }

  onGoToComplateModule() {
    this.controller.next();
  }

  @HostListener('window:resize')
  onResize() {
    setTimeout(() => this.videobgEnlarge(), 10);
  }

  videobgEnlarge() {
    const videoBgAspect = document.querySelector<HTMLElement>('.fullScreen .videobg-aspect');
    const videoBgWidth = document.querySelector<HTMLElement>('.fullScreen .videobg-width');
    const controlContainer = document.querySelector<HTMLElement>('.fullScreen .videobg-width .controls-container');
    if (!videoBgAspect || !videoBgWidth || !controlContainer) return;

    const videoAspect = videoBgAspect.offsetHeight / videoBgAspect.offsetWidth;
    const windowAspect = window.innerHeight / window.innerWidth;
    const calculated = 100 * (videoAspect - (windowAspect - 0.06));
    controlContainer.style.bottom = Math.max(calculated, 10) + '%';

    if (windowAspect > videoAspect) {
      const calculationWith = (windowAspect / videoAspect) * 100;
      videoBgWidth.style.width = calculationWith + '%';
      if (calculationWith > 100) {
        const leftPosition = calculationWith - 100;
        controlContainer.style.width = (100 - leftPosition) + '%';
        controlContainer.style.left = (leftPosition / 2) + '%';
      }
    } else {
      if (this.underPlay) {
        videoBgWidth.style.width = '100%';
      }
    }
  }

  @HostListener('document:mousemove')
  onMouseMove() {
    if (this.underPlay) {
      this.lastEvent = new Date().getTime();
      this.fadeInControls();
    }
  }

  fadeOutControls() {
    const el = document.querySelector<HTMLElement>('.controls-container');
    if (el && el.style.opacity !== '0') {
      el.style.opacity = '0';
    }
  }

  fadeInControls() {
    const el = document.querySelector<HTMLElement>('.controls-container');
    if (el && el.style.opacity !== '1') {
      el.style.opacity = '1';
    }
  }
}
